import React from "react";
import { Box, Button, Typography, CircularProgress } from "@mui/material";

/**
 * Shared UI for embedded-signup WhatsApp contact/history sync (profile dialog + settings).
 */
export default function WhatsappEmbeddedSyncContent({
  isContact: isContactSync,
  isHistory: isHistorySync,
  chunkData,
  onSync,
}) {
  const progress = chunkData?.progress || 0;

  return (
    <>
      <Box sx={{ mb: 2 }}>
        <Box sx={{ mt: 1, ml: 1 }}>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ lineHeight: 1.7 }}
          >
            • Media messages older than 14 days will not be synced.
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ lineHeight: 1.7 }}
          >
            • Only Image, Audio and Document messages will be synced.
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ lineHeight: 1.7, mt: 1 }}
          >
            • If sync fails or is incomplete, disconnect your business from the
            WhatsApp Business app, re-login using embedded signup, and try again.
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ lineHeight: 1.7, mt: 1 }}
          >
            • To sync chat history, ensure you allow sync while connecting your
            WhatsApp Business app.
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          display: "grid",
          gap: 2,
          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
          mb: 2,
        }}
      >
        <Button
          variant={isContactSync === "completed" ? "contained" : "outlined"}
          color={isContactSync === "completed" ? "success" : "primary"}
          disabled={
            isContactSync === "completed" || isContactSync === "in-progress"
          }
          onClick={() => onSync("contacts")}
          fullWidth
          sx={{ height: 48, textTransform: "none" }}
        >
          {isContactSync === "completed"
            ? "Contacts Synced"
            : isContactSync === "in-progress"
              ? "Syncing Contacts..."
              : "Sync Contacts"}
        </Button>

        <Button
          variant={isHistorySync === "completed" ? "contained" : "outlined"}
          color={isHistorySync === "completed" ? "success" : "primary"}
          disabled={
            isContactSync !== "completed" ||
            isHistorySync === "completed" ||
            isHistorySync === "in-progress"
          }
          onClick={() => onSync("history")}
          fullWidth
          sx={{ height: 48, textTransform: "none" }}
        >
          {isHistorySync === "completed"
            ? "History Synced"
            : isHistorySync === "in-progress"
              ? "Syncing History..."
              : "Sync History"}
        </Button>
      </Box>

      {isHistorySync === "in-progress" && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 1.5,
            mt: 1,
            py: 2,
            borderRadius: 2,
            border: "1px solid",
            borderColor: "divider",
            backgroundColor: "background.paper",
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Syncing history
          </Typography>

          <Typography variant="body2" color="text.secondary">
            Progress: {Math.round(progress)}%
          </Typography>

          <Box sx={{ position: "relative", display: "inline-flex" }}>
            <CircularProgress
              variant="determinate"
              value={progress}
              size={60}
            />
            <Box
              sx={{
                top: 0,
                left: 0,
                bottom: 0,
                right: 0,
                position: "absolute",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography
                variant="caption"
                component="div"
                color="text.secondary"
              >
                {`${Math.round(progress)}%`}
              </Typography>
            </Box>
          </Box>
        </Box>
      )}
    </>
  );
}
